import React, { type ButtonHTMLAttributes, type ComponentType, type ReactElement } from 'react';

import { type SerializedStyles, css } from '@emotion/react';

type IconButtonSize = 'm' | 'l';

export const ICON_BUTTON_SIZES: Record<IconButtonSize, { buttonSize: number; iconSize: number }> = {
    m: { buttonSize: 28, iconSize: 18 },
    l: { buttonSize: 30, iconSize: 18 },
};

const getIconButtonStyle = (size: IconButtonSize): SerializedStyles => {
    const { buttonSize, iconSize } = ICON_BUTTON_SIZES[size];

    return css({
        width: buttonSize,
        height: buttonSize,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexShrink: 0,
        borderRadius: 'var(--radius-small-component, 8px)',
        svg: {
            fill: '#647491',
            width: iconSize,
            height: iconSize,
        },

        '&:focus': {
            outline: 'none',
        },

        '&:not(:disabled)': {
            cursor: 'pointer',

            '&:hover, &:focus': {
                background: 'var(--custom-action-color-background)',
                svg: {
                    fill: 'var(--custom-action-color)',
                },
            },
            '&:active': {
                background: 'var(--custom-action-color-background-active)',
            },
        },

        '&:disabled': {
            cursor: 'default',
            svg: {
                fill: '#ACB8CB',
            },
        },
    });
};

interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
    icon: ComponentType;
    size?: IconButtonSize;
}

const IconButton = ({ icon: Icon, size = 'l', ...rest }: IconButtonProps): ReactElement => (
    <button type="button" css={getIconButtonStyle(size)} {...rest}>
        <Icon />
    </button>
);

export default IconButton;
